/**
 * Apontamento de cliente operado no ERP diante do TiFlux (ADR-013, 03/10: "o
 * ERP vence"). As peças que a aba de apontamentos, o indicador "Mudou no
 * TiFlux" do cabeçalho e o "Puxar do TiFlux" compartilham: como um lado do
 * apontamento se escreve, a marca "editado no ERP" e o cartão da
 * pendência de apontamento.
 */
import type { Appointment, TifluxAppointmentPending, TifluxAppointmentValue } from '@erp/shared';
import { Badge } from '@/components/ui/misc';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn, formatMinutes } from '@/lib/utils';
import { formatDate, formatTime } from './detail-format';
import { durationMinutes } from './ticket-format';

/** "Lídia · 02/09/2026 13:00–14:15 (01:15)" — quem e quando */
export function appointmentPeriodText(v: TifluxAppointmentValue): string {
  const period = `${formatDate(v.startedAt)} ${formatTime(v.startedAt)}–${formatTime(v.endedAt)}`;
  const duration = formatMinutes(durationMinutes(v.startedAt, v.endedAt));
  return `${v.userName ?? '—'} · ${period} (${duration})`;
}

/**
 * Um lado do apontamento em duas linhas — quem e quando, e a descrição
 * (até duas linhas, sem cortar no meio da palavra). A descrição é justamente o
 * que a pessoa compara: numa linha truncada ela sumiria.
 */
export function AppointmentValueLines({
  side,
  value,
  strong,
}: {
  side: 'TiFlux' | 'ERP';
  value: TifluxAppointmentValue;
  strong?: boolean;
}) {
  return (
    <div className={cn('min-w-0 text-xs', !strong && 'text-muted-foreground')}>
      <p className="min-w-0 break-words">
        {side}: {strong ? <strong>{appointmentPeriodText(value)}</strong> : appointmentPeriodText(value)}
      </p>
      <p className="line-clamp-2 min-w-0 break-words pl-3" title={value.description ?? undefined}>
        {value.description?.trim() || 'sem descrição'}
      </p>
    </div>
  );
}

/** "Editado no ERP por Bia em 03/10/2026 14:20." — null quando não é o caso */
export function erpEditedNote(a: Pick<Appointment, 'fromTiflux' | 'erpEdited'>): string | null {
  if (a.fromTiflux !== true || !a.erpEdited) return null;
  const at = new Date(a.erpEdited.at);
  const who = a.erpEdited.by?.name ? ` por ${a.erpEdited.by.name}` : '';
  return `Editado no ERP${who} em ${formatDate(at)} ${formatTime(at)}.`;
}

/**
 * A marca "editado no ERP" na linha do apontamento vindo do TiFlux: quem e
 * quando no tooltip, e o que ela significa — o TiFlux não o regrava mais.
 */
export function ErpEditedMark({ note }: { note: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span tabIndex={0} className="inline-flex shrink-0" aria-label={note}>
          <Badge variant="info">editado no ERP</Badge>
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-72">
        {note} O TiFlux não regrava mais este apontamento; se ele mudar lá, vira pendência para
        alguém decidir.
      </TooltipContent>
    </Tooltip>
  );
}

/** a pendência de UM apontamento: os dois lados e desde quando */
export function AppointmentPendingCard({ pending }: { pending: TifluxAppointmentPending }) {
  return (
    <div className="min-w-0 space-y-1 rounded-md border px-3 py-2">
      <p className="font-medium">Apontamento</p>
      <AppointmentValueLines side="TiFlux" value={pending.tiflux} strong />
      <AppointmentValueLines side="ERP" value={pending.erp} />
      <p className="text-xs text-muted-foreground">
        Visto em {new Date(pending.createdAt).toLocaleString('pt-BR')}
      </p>
    </div>
  );
}
